// Configuración → Farmacias (§1.3): el catálogo del Colegio. Tabla con buscador, alta y
// edición en un modal, borrar con confirmación y foto arrastrándola sobre la fila.
// Toda acción muestra guardando / guardado / error (§4.8, P-13).
import React, { useMemo, useState } from "react";
import { Pencil, Plus, Search, Store, Trash2 } from "lucide-react";
import type { Pharmacy } from "../types";
import { adminFetch } from "../api";
import { compressImage } from "../imageOptimizer";
import { Alert, Button, Empty, Field, ICON, IconButton, Input, Modal, Skeleton, Spinner, useToast } from "./ui";
import { ConfigPage, type CfgView } from "./ConfigPage";
import { ImageUploader } from "./ImageUploader";
import { FotoHistoria } from "./FotoHistoria";
import type { AdminRole } from "./views";

type PharmacyForm = {
  id?: string;
  nombre: string;
  direccion: string;
  telefono: string;
  whatsapp: string;
  locationUrl: string;
  fotoUrl: string;
  colorHex: string;
};

const EMPTY_FORM: PharmacyForm = {
  nombre: "",
  direccion: "",
  telefono: "",
  whatsapp: "",
  locationUrl: "",
  fotoUrl: "",
  colorHex: "#10b981",
};

// .tr de la maqueta: cuatro columnas en escritorio, nombre y acciones en el celular
const ROW =
  "grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3.5 border-b border-adm-border py-2.5 pl-4 pr-3 text-sm last:border-b-0 md:grid-cols-[minmax(0,1.4fr)_minmax(0,1.4fr)_minmax(0,1fr)_auto]";

async function errorFrom(res: Response, fallback: string): Promise<string> {
  const data = await res.json().catch(() => null);
  return (data && data.error) || `${fallback} (el servidor respondió ${res.status})`;
}

export function FarmaciasPage({
  pharmacies,
  loading,
  loadFailed,
  role,
  onNavigate,
  onReload,
}: {
  pharmacies: Pharmacy[];
  /** primera carga en curso */
  loading: boolean;
  /** la última carga de datos falló */
  loadFailed: boolean;
  role: AdminRole;
  onNavigate: (view: CfgView) => void;
  onReload: () => Promise<unknown>; // fetchData devuelve boolean desde la Ficha 4
}) {
  const toast = useToast();
  const [query, setQuery] = useState("");
  const [form, setForm] = useState<PharmacyForm | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [toDelete, setToDelete] = useState<Pharmacy | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [photoDrop, setPhotoDrop] = useState<{ pharm: Pharmacy; file: File } | null>(null);
  const [uploadingId, setUploadingId] = useState<string | null>(null);

  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    return pharmacies
      .filter((p) => !q || p.nombre.toLowerCase().includes(q) || (p.direccion || "").toLowerCase().includes(q))
      .sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [pharmacies, query]);

  const set = (patch: Partial<PharmacyForm>) => setForm((f) => (f ? { ...f, ...patch } : f));

  const openNew = () => {
    setFormError(null);
    setForm({ ...EMPTY_FORM });
  };

  const openEdit = (p: Pharmacy) => {
    setFormError(null);
    setForm({
      id: p.id,
      nombre: p.nombre,
      direccion: p.direccion,
      telefono: p.telefono || "",
      whatsapp: p.whatsapp || "",
      locationUrl: p.locationUrl || "",
      fotoUrl: p.fotoUrl || "",
      colorHex: p.colorHex || "#10b981",
    });
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form) return;
    if (!form.nombre.trim() || !form.direccion.trim()) {
      setFormError("Falta el nombre o la dirección.");
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const res = await adminFetch("/api/pharmacies", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, nombre: form.nombre.trim(), direccion: form.direccion.trim() }),
      });
      if (!res.ok) throw new Error(await errorFrom(res, "No se guardó la farmacia"));
      await onReload();
      setForm(null);
      toast.show("Farmacia guardada");
    } catch (err: any) {
      setFormError(err?.message || "No se guardó la farmacia.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      const res = await adminFetch(`/api/pharmacies/${encodeURIComponent(toDelete.id)}`, { method: "DELETE" });
      if (!res.ok) throw new Error(await errorFrom(res, "No se borró la farmacia"));
      await onReload();
      toast.show(`Borrada: ${toDelete.nombre}`);
      setToDelete(null);
    } catch (err: any) {
      toast.show(err?.message || "No se borró la farmacia.", "error");
    } finally {
      setDeleting(false);
    }
  };

  const onDropPhoto = (pharm: Pharmacy, e: React.DragEvent) => {
    e.preventDefault();
    setDragOverId(null);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.show("Eso no es una imagen. Arrastrá un PNG, JPG, WEBP o GIF.", "error");
      return;
    }
    setPhotoDrop({ pharm, file });
  };

  const uploadPhoto = async () => {
    if (!photoDrop) return;
    const { pharm, file } = photoDrop;
    setPhotoDrop(null);
    setUploadingId(pharm.id);
    try {
      let fileToUpload: File = file;
      try {
        fileToUpload = (await compressImage(file, { maxWidth: 1200, maxHeight: 1200, quality: 0.75, convertTo: "image/webp" })).file;
      } catch {
        // si no se puede optimizar, se sube la original
      }
      const formData = new FormData();
      formData.append("photo", fileToUpload);
      const up = await adminFetch("/api/upload-photo", { method: "POST", body: formData });
      if (!up.ok) throw new Error(await errorFrom(up, "No se pudo subir la foto"));
      const { url } = await up.json();
      const res = await adminFetch("/api/pharmacies", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...pharm, fotoUrl: url }),
      });
      if (!res.ok) throw new Error(await errorFrom(res, "La foto subió pero no quedó en la farmacia"));
      await onReload();
      toast.show(`Foto de ${pharm.nombre} actualizada`);
    } catch (err: any) {
      toast.show(err?.message || "No se pudo cambiar la foto.", "error");
    } finally {
      setUploadingId(null);
    }
  };

  const addButton = (
    <Button variant="primary" icon={<Plus {...ICON} />} onClick={openNew}>
      Agregar farmacia
    </Button>
  );

  let body: React.ReactNode;
  if (loading && pharmacies.length === 0) {
    body = (
      <div className="flex flex-col gap-1.5" aria-busy="true">
        <Skeleton className="h-[52px]" />
        <Skeleton className="h-[52px]" />
        <Skeleton className="h-[52px]" />
      </div>
    );
  } else if (loadFailed && pharmacies.length === 0) {
    body = (
      <Alert tone="red" action={<Button size="sm" onClick={() => onReload()}>Reintentar</Button>}>
        No se pudieron cargar las farmacias. Revisá la conexión y reintentá.
      </Alert>
    );
  } else if (pharmacies.length === 0) {
    body = (
      <Empty icon={Store} title="Sin farmacias" action={addButton}>
        Agregá las farmacias del Colegio para poder asignarlas a los turnos.
      </Empty>
    );
  } else {
    body = (
      <>
        <div className="relative">
          <Search {...ICON} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-adm-t40" aria-hidden="true" />
          <Input
            type="search"
            aria-label="Buscar farmacia"
            placeholder="Buscar por nombre o dirección"
            value={query}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
            style={{ paddingLeft: 38 }}
          />
        </div>
        {loadFailed && <Alert tone="amber">No se pudo actualizar la lista: lo que ves puede estar desactualizado.</Alert>}
        <p className="text-xs text-adm-t55 max-md:hidden">Para cambiar la foto, arrastrala sobre la fila de la farmacia o tocá el lápiz.</p>
        {list.length === 0 ? (
          <Empty size="sm" title="Ninguna coincide">
            No hay farmacias con «{query.trim()}».
          </Empty>
        ) : (
          <div className="overflow-hidden rounded-[14px] border border-adm-border bg-adm-surface">
            <div className={`${ROW} min-h-10 bg-adm-card`}>
              <span className="text-xs font-medium text-adm-t55">Farmacia</span>
              <span className="text-xs font-medium text-adm-t55 max-md:hidden">Dirección</span>
              <span className="text-xs font-medium text-adm-t55 max-md:hidden">Teléfono</span>
              <span />
            </div>
            {list.map((p) => {
              const over = dragOverId === p.id;
              return (
                <div
                  key={p.id}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragOverId(p.id);
                  }}
                  onDragLeave={() => setDragOverId((id) => (id === p.id ? null : id))}
                  onDrop={(e) => onDropPhoto(p, e)}
                  className={`${ROW} min-h-[52px] transition-colors ${over ? "bg-adm-acento-suave" : "hover:bg-adm-t5"}`}
                >
                  <span className="truncate font-medium text-adm-text" title={p.nombre} translate="no">
                    {p.nombre}
                  </span>
                  <span className="truncate text-adm-t55 max-md:hidden" title={p.direccion}>
                    {p.direccion}
                  </span>
                  <span className="truncate font-mono text-xs text-adm-t55 max-md:hidden">{p.telefono}</span>
                  <span className="flex items-center">
                    {uploadingId === p.id ? (
                      <span className="flex items-center gap-1.5 px-2 text-xs text-adm-t55">
                        <Spinner size={14} />
                        Subiendo foto…
                      </span>
                    ) : over ? (
                      <span className="px-2 text-xs font-medium text-adm-acento-texto">Soltá la foto</span>
                    ) : (
                      <>
                        <IconButton label={`Editar ${p.nombre}`} onClick={() => openEdit(p)}>
                          <Pencil {...ICON} />
                        </IconButton>
                        <IconButton label={`Borrar ${p.nombre}`} tone="danger" onClick={() => setToDelete(p)}>
                          <Trash2 {...ICON} />
                        </IconButton>
                      </>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </>
    );
  }

  return (
    <ConfigPage view="cfg:farmacias" role={role} onNavigate={onNavigate} actions={pharmacies.length > 0 ? addButton : undefined}>
      {body}

      <Modal
        open={form !== null}
        onClose={() => !saving && setForm(null)}
        title={form?.id ? "Editar farmacia" : "Agregar farmacia"}
        footer={
          <>
            <Button onClick={() => setForm(null)} disabled={saving}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit" form="farmacia-form" loading={saving}>
              {saving ? "Guardando…" : "Guardar"}
            </Button>
          </>
        }
      >
        {form && (
          <form id="farmacia-form" onSubmit={save} className="flex flex-col gap-4">
            <Field label="Nombre" htmlFor="ph-nombre">
              <Input id="ph-nombre" data-autofocus required placeholder="Ej. Farmacia Central" value={form.nombre} onChange={(e: React.ChangeEvent<HTMLInputElement>) => set({ nombre: e.target.value })} />
            </Field>
            <Field label="Dirección" htmlFor="ph-direccion">
              <Input id="ph-direccion" required placeholder="Ej. Av. Colón 100" value={form.direccion} onChange={(e: React.ChangeEvent<HTMLInputElement>) => set({ direccion: e.target.value })} />
            </Field>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Field label="Teléfono" htmlFor="ph-telefono">
                <Input id="ph-telefono" inputMode="tel" placeholder="Ej. 2494636900" value={form.telefono} onChange={(e: React.ChangeEvent<HTMLInputElement>) => set({ telefono: e.target.value })} />
              </Field>
              <Field label="WhatsApp de consulta" htmlFor="ph-whatsapp" hint="Opcional. Si lo cargás, la web muestra el botón «Enviar WhatsApp».">
                <Input id="ph-whatsapp" inputMode="tel" placeholder="Ej. 5492494112233" value={form.whatsapp} onChange={(e: React.ChangeEvent<HTMLInputElement>) => set({ whatsapp: e.target.value })} />
              </Field>
            </div>
            <Field label="Link de Google Maps" htmlFor="ph-maps" hint="Opcional. Si lo dejás vacío, se arma solo con el nombre y la dirección.">
              <Input id="ph-maps" inputMode="url" placeholder="https://maps.app.goo.gl/…" value={form.locationUrl} onChange={(e: React.ChangeEvent<HTMLInputElement>) => set({ locationUrl: e.target.value })} />
            </Field>
            <ImageUploader label="Foto" value={form.fotoUrl} onChange={(url) => set({ fotoUrl: url })} idPrefix="catalog-pharmacy" />
            <Field label="O pegá el link de la foto" htmlFor="ph-foto" hint="Ideal entre 400 y 800 px de ancho. En la web se ve atenuada detrás de los datos.">
              <Input id="ph-foto" inputMode="url" placeholder="https://…" value={form.fotoUrl} onChange={(e: React.ChangeEvent<HTMLInputElement>) => set({ fotoUrl: e.target.value })} />
            </Field>
            {form.id ? (
              <FotoHistoria pharmacyId={form.id} />
            ) : (
              <p className="text-xs text-adm-t55">Guardá la farmacia primero para cargarle la foto de historia.</p>
            )}
            <Field label="Color de la tarjeta" htmlFor="ph-color">
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  aria-label="Elegir el color de la tarjeta"
                  value={/^#[0-9a-f]{6}$/i.test(form.colorHex) ? form.colorHex : "#10b981"}
                  onChange={(e) => set({ colorHex: e.target.value })}
                  className="h-[42px] w-12 shrink-0 cursor-pointer rounded-[10px] border border-adm-border-2 bg-adm-surface p-1 max-md:h-11"
                />
                <Input id="ph-color" mono placeholder="#10b981" value={form.colorHex} onChange={(e: React.ChangeEvent<HTMLInputElement>) => set({ colorHex: e.target.value })} />
              </div>
            </Field>
            {formError && <Alert tone="red">{formError}</Alert>}
          </form>
        )}
      </Modal>

      <Modal
        open={toDelete !== null}
        onClose={() => !deleting && setToDelete(null)}
        size="sm"
        title={`¿Borrar ${toDelete?.nombre ?? "la farmacia"}?`}
        footer={
          <>
            <Button onClick={() => setToDelete(null)} disabled={deleting}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={remove} loading={deleting}>
              Borrar farmacia
            </Button>
          </>
        }
      >
        <p className="text-[13px] leading-[1.55] text-adm-t55">
          Sale del catálogo y deja de aparecer en los días donde estaba de turno. No se puede deshacer.
        </p>
      </Modal>

      <Modal
        open={photoDrop !== null}
        onClose={() => setPhotoDrop(null)}
        size="sm"
        title={`¿Cambiar la foto de ${photoDrop?.pharm.nombre ?? "la farmacia"}?`}
        footer={
          <>
            <Button onClick={() => setPhotoDrop(null)}>Cancelar</Button>
            <Button variant="primary" onClick={uploadPhoto}>
              Cambiar foto
            </Button>
          </>
        }
      >
        <p className="text-[13px] leading-[1.55] text-adm-t55">
          {photoDrop?.file.name} reemplaza la foto que tiene ahora. Se optimiza antes de subirla.
        </p>
      </Modal>
    </ConfigPage>
  );
}
